/** localStorage key holding publication drafts. */
export const PUB_DRAFTS_KEY = 'wikistar:publication-drafts';

/**
 * Drafts for publications — works in progress for the
 * `PublicationEditPage`'s metadata and all of its sections, keyed by a
 * generated id rather than the publication's slug so a title rename
 * mid-writing doesn't merge or lose drafts.
 */
export interface PublicationDraftSection {
  /**
   * Stable React list key. Render-only bookkeeping: it's persisted so a
   * resumed draft keeps stable keys across reorders within the session,
   * but it carries no document meaning and is regenerated if lost.
   */
  key: string;
  /**
   * The section's own `d` tag, if it has been published before. Kept so
   * re-editing updates the same addressable `kind:30041` event instead
   * of forking a new section every time its title changes.
   */
  d: string;
  title: string;
  content: string;
  /**
   * The markup format this section is edited in. Stored as a plain
   * `string` because localStorage is untrusted — callers narrow it with
   * `isContentFormat` and fall back to detection on anything else.
   */
  format: string | undefined;
  /** True once this section has been published at least once. */
  published: boolean;
}

export interface PublicationDraftAuthor {
  key: string;
  name: string;
  role: string;
}

export interface PublicationDraftIdentifier {
  key: string;
  type: string;
  value: string;
}

export interface PublicationDraft {
  id: string;
  pubkey: string;
  /**
   * The `d` tag of the already-published publication this draft is
   * revising, or `undefined` for a draft that has never been published.
   *
   * Kept separately from the title because the two diverge: `d` is
   * fixed once a publication exists (changing it would fork a new
   * publication rather than update the original), while the title is
   * freely editable. Resuming uses this to decide between
   * `/p/:d/edit` and `/p/new`, so a brand-new draft doesn't send the
   * editor off to fetch a publication that isn't on any relay.
   */
  d?: string;
  title: string;
  summary: string;
  type: string;
  image: string;
  version: string;
  publishedOn: string;
  publishedBy: string;
  source: string;
  topics: string;
  authors: PublicationDraftAuthor[];
  identifiers: PublicationDraftIdentifier[];
  sections: PublicationDraftSection[];
  createdAt: number;
  updatedAt: number;
}

export type PublicationDraftStore = Record<string, PublicationDraft>;

/**
 * A publication draft's display title.
 *
 * Unlike an article draft there's no slug to fall back on — a
 * publication's `d` tag is only computed at publish time — so an
 * untitled draft falls back to its first named section, then to a
 * placeholder, rather than showing an empty row the user can't identify.
 */
export function publicationDraftTitle(draft: PublicationDraft): string {
  const title = draft.title.trim();
  if (title) return title;

  const firstSection = draft.sections.find((section) => section.title.trim());
  if (firstSection) return firstSection.title.trim();

  return 'Untitled publication';
}

/** True when a publication draft has nothing worth keeping. */
export function isPublicationDraftEmpty(
  draft: Pick<PublicationDraft, 'title' | 'summary' | 'sections'>,
): boolean {
  return (
    !draft.title.trim() &&
    !draft.summary.trim() &&
    draft.sections.every((section) => !section.title.trim() && !section.content.trim())
  );
}
